import type {FormKitNode} from '@formkit/core';
import {_maybeWrapWithFormKitSchema} from '@bttr-devs/nuxt-layer-sofie/utils/formkit';
import type {UseSofieSchemaOptions, UseSofieBaseSchemaReturn} from '@bttr-devs/nuxt-layer-sofie';

type Credentials = {
  email: string
  password: string
  twoFactorCode?: string
}

type UseSofieLogin = {
  login: (credentials: Credentials, formNode: FormKitNode) => Promise<void>;
  errors: Ref<string[]>,
  stage: Ref<'2fa' | 'login'>;
};

export function useSofieLogin(): UseSofieLogin {
  const route = useRoute();
  const router = useRouter();
  const {signIn} = useAuth();
  const {t} = useI18n();

  const errors = ref<string[]>([]);
  const stage = ref<'login' | '2fa'>('login');

  async function login(credentials: Credentials) {
    errors.value = [];

    const response = await signIn('sofie', {
      ...credentials,
      redirect: false,
      callbackUrl: (route.query.callbackUrl as string | undefined) ?? '/',
    });

    if (response.error) {
      if (response.error === '2fa') {
        if (!credentials.twoFactorCode) {
          stage.value = '2fa';
          return;
        }

        errors.value.push(t('sofie.auth.login.invalid_2fa'));
      } else if (response.error === 'validation') {
        errors.value.push(t('sofie.auth.login.invalid_credentials'));
      } else {
        console.error(response.error);
      }

      return;
    }

    redirect(response.url);
  }

  function redirect(to: string) {
    const url = new URL(to);

    if (window.location.host !== url.host) {
      window.location.href = url.href;
    } else if (url.pathname === route.path) {
      router.push('/');
    } else {
      router.push(url.pathname);
    }
  }

  return {
    errors,
    login,
    stage,
  };
}

export function useSofieLoginSchema(options: UseSofieSchemaOptions = {}): UseSofieBaseSchemaReturn {
  const {
    errors,
    login,
    stage,
  } = useSofieLogin();
  const {t} = useI18n();

  const schema = {
    if: '$stage === login',
    then: [
      {
        $formkit: 'email',
        label: t('sofie.auth.login.email'),
        name: 'email',
        key: 'email',
        validation: 'required:trim|email',
        autocomplete: 'username',
      },
      {
        $formkit: 'password',
        label: t('sofie.auth.login.password'),
        name: 'password',
        key: 'password',
        validation: 'required:trim',
        autocomplete: 'current-password',
      },
    ],
    else: [
      {
        $formkit: 'text',
        label: t('sofie.auth.login.2fa_code'),
        name: 'twoFactorCode',
        key: 'twoFactorCode',
        validation: 'required:trim',
        autocomplete: 'one-time-code',
      },
    ],
  };

  return {
    data: {
      errors,
      stage,
      submit: login,
    },
    schema: _maybeWrapWithFormKitSchema(!options.excludeForm, schema, {
      $formkit: 'form',
      errors: '$errors',
      method: 'post',
      submitLabel: t('sofie.auth.login.submit'),
      onSubmit: '$submit',
      preserve: true,
    }),
  };
}
